const TOOLTIP_MARGIN = 8;
const TOOLTIP_MIN_LEFT = 8;

const resolveContent = (el, binding) => {
	let value = binding?.value;
	if (typeof value === 'function') {
		try {
			value = value();
		} catch (error) {
			console.error('tooltip directive: failed to evaluate binding function', error);
		}
	}
	if (value === undefined || value === null || value === '') {
		const input = el.querySelector('input, textarea, [contenteditable="true"]');
		if (input) {
			value = input.value ?? input.textContent ?? '';
		} else {
			value = el.textContent?.trim() ?? '';
		}
	}
	return String(value || '').trim();
};

const positionTooltip = (tooltipEl, targetRect) => {
	const { innerWidth, scrollX, scrollY } = window;
	const tooltipRect = tooltipEl.getBoundingClientRect();
	let top = targetRect.top + scrollY - tooltipRect.height - TOOLTIP_MARGIN;
	const minTop = scrollY + TOOLTIP_MARGIN;
	if (top < minTop) {
		top = targetRect.bottom + scrollY + TOOLTIP_MARGIN;
		tooltipEl.dataset.placement = 'bottom';
	} else {
		tooltipEl.dataset.placement = 'top';
	}
	let left = targetRect.left + scrollX + targetRect.width / 2 - tooltipRect.width / 2;
	const maxLeft = scrollX + innerWidth - tooltipRect.width - TOOLTIP_MARGIN;
	left = Math.max(scrollX + TOOLTIP_MIN_LEFT, Math.min(left, maxLeft));
	tooltipEl.style.top = `${top}px`;
	tooltipEl.style.left = `${left}px`;
};

const isOverflowing = (el) => {
	const input = el.querySelector('input, textarea, [contenteditable="true"]');
	if (input && input.tagName === 'INPUT') {
		// For input elements, create a temporary span to measure text width
		const span = document.createElement('span');
		const style = window.getComputedStyle(input);
		span.style.font = style.font;
		span.style.fontSize = style.fontSize;
		span.style.fontFamily = style.fontFamily;
		span.style.fontWeight = style.fontWeight;
		span.style.letterSpacing = style.letterSpacing;
		span.style.visibility = 'hidden';
		span.style.position = 'absolute';
		span.style.whiteSpace = 'nowrap';
		span.textContent = input.value;
		document.body.appendChild(span);
		const textWidth = span.offsetWidth;
		document.body.removeChild(span);
		const inputPadding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
		return textWidth > (input.clientWidth - inputPadding);
	}
	const target = input || el;
	return target.scrollWidth > target.clientWidth || target.scrollHeight > target.clientHeight;
};

const attachListeners = (el, state) => {
	const show = () => {
		const text = resolveContent(el, state.binding);
		if (!text || !isOverflowing(el)) {
			state.visible = false;
			return;
		}
		state.tooltipEl.textContent = text;
		state.tooltipEl.style.visibility = 'hidden';
		state.tooltipEl.style.opacity = '0';
		state.tooltipEl.style.display = 'block';
		state.tooltipEl.style.transform = 'translateY(4px)';
		requestAnimationFrame(() => {
			positionTooltip(state.tooltipEl, el.getBoundingClientRect());
			state.tooltipEl.style.visibility = 'visible';
			state.tooltipEl.style.opacity = '1';
			state.tooltipEl.style.transform = 'translateY(0)';
		});
		state.visible = true;
	};

	const hide = () => {
		if (!state.visible) return;
		state.visible = false;
		state.tooltipEl.style.opacity = '0';
		state.tooltipEl.style.transform = 'translateY(4px)';
		state.hideTimer = window.setTimeout(() => {
			state.tooltipEl.style.display = 'none';
			state.tooltipEl.style.visibility = 'hidden';
		}, 150);
	};

	const update = () => {
		if (!state.visible) return;
		positionTooltip(state.tooltipEl, el.getBoundingClientRect());
	};

	state.showHandler = show;
	state.hideHandler = hide;
	state.updateHandler = update;

	el.addEventListener('mouseenter', show);
	el.addEventListener('mouseleave', hide);
	el.addEventListener('focusin', show);
	el.addEventListener('focusout', hide);
	window.addEventListener('scroll', update, true);
	window.addEventListener('resize', update);
};

const detachListeners = (el, state) => {
	el.removeEventListener('mouseenter', state.showHandler);
	el.removeEventListener('mouseleave', state.hideHandler);
	el.removeEventListener('focusin', state.showHandler);
	el.removeEventListener('focusout', state.hideHandler);
	window.removeEventListener('scroll', state.updateHandler, true);
	window.removeEventListener('resize', state.updateHandler);
	if (state.hideTimer) {
		clearTimeout(state.hideTimer);
	}
};

const tooltipDirective = {
	mounted(el, binding) {
		if (typeof window === 'undefined') return;
		const tooltipEl = document.createElement('div');
		tooltipEl.className = 'noc-tooltip';
		tooltipEl.role = 'tooltip';
		tooltipEl.style.position = 'fixed';
		tooltipEl.style.pointerEvents = 'none';
		tooltipEl.style.opacity = '0';
		tooltipEl.style.visibility = 'hidden';
		tooltipEl.style.transform = 'translateY(4px)';
		tooltipEl.style.transition = 'opacity 150ms ease, transform 150ms ease';
		tooltipEl.style.display = 'none';
		document.body.appendChild(tooltipEl);
		const state = {
			tooltipEl,
			binding,
			visible: false,
			hideTimer: null,
		};
		el._nocTooltipState = state;
		attachListeners(el, state);
	},
	updated(el, binding) {
		if (!el._nocTooltipState) return;
		el._nocTooltipState.binding = binding;
		if (el._nocTooltipState.visible) {
			const text = resolveContent(el, binding);
			if (!text) {
				el._nocTooltipState.hideHandler();
			} else {
				el._nocTooltipState.tooltipEl.textContent = text;
				positionTooltip(el._nocTooltipState.tooltipEl, el.getBoundingClientRect());
			}
		}
	},
	unmounted(el) {
		const state = el._nocTooltipState;
		if (!state) return;
		detachListeners(el, state);
		if (state.tooltipEl && state.tooltipEl.parentNode) {
			state.tooltipEl.parentNode.removeChild(state.tooltipEl);
		}
		delete el._nocTooltipState;
	},
};

export default tooltipDirective;
